import { useContext } from "react";
import { CustomizationModalStateContext } from "../../../../Shared/components/CustomizeModal/CustomizationContext";
import { useLogging } from "../../../../Shared/hooks/useLogging/useLogging";
import { ActionTypes } from "../../../OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "../../../OrderFlowProvider/OrderFlowContext";
import { ModuleNames, ProductFilterDays } from "../../../OrderFlowProvider/OrderFlowTypes";
import { GetFilterDayId } from "../../../utils/orderFlowUtils";
import { useEcommerce } from "../../../../Shared/hooks/useEcommerce/useEcommerce";
const FilterProducts = (products, filterProductsOnDay, daysOfWeek, cart, deliverySchedule) => {
    const filterDayId = GetFilterDayId(filterProductsOnDay, cart, deliverySchedule);
    const productList = products.filter((product) => {
        let shouldShowProductForFilterDay = true;
        let shouldShowProductForSelectedDay = true;
        if (filterProductsOnDay && filterProductsOnDay !== ProductFilterDays.firstAvailable) {
            //Product has to match day on the module config
            shouldShowProductForFilterDay = product.DayOfWeek === filterDayId;
            //If DaysOfWeekModule has a day(s) selected, Products should only show for the selected day(s)
            if ((daysOfWeek === null || daysOfWeek === void 0 ? void 0 : daysOfWeek.length) > 0) {
                shouldShowProductForSelectedDay = daysOfWeek.includes(product.DayOfWeek);
            }
        }
        return shouldShowProductForFilterDay && shouldShowProductForSelectedDay;
    });
    if (filterProductsOnDay === ProductFilterDays.firstAvailable) {
        var addedIds = new Set();
        var result = [];
        productList.forEach((product) => {
            if (!addedIds.has(product.productId) &&
                (filterDayId == -1 || product.DayOfWeek == -1 || product.DayOfWeek == filterDayId)) {
                addedIds.add(product.productId);
                result.push(product);
            }
        });
        return result;
    }
    return productList;
};
const HandleProductIncrement = (products, filterProductsOnDay, isEditFlow, companyUsesKlaviyo, sku, cart, deliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, reactPixelKey) => (productId, dayOfWeek) => {
    HandleProductChange(products, filterProductsOnDay, isEditFlow, companyUsesKlaviyo, sku, productId, dayOfWeek, true, cart, deliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, reactPixelKey);
};
const HandleProductDecrement = (products, filterProductsOnDay, isEditFlow, companyUsesKlaviyo, sku, cart, deliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, reactPixelKey) => (productId, dayOfWeek) => {
    HandleProductChange(products, filterProductsOnDay, isEditFlow, companyUsesKlaviyo, sku, productId, dayOfWeek, false, cart, deliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, reactPixelKey);
};
/**
 * Ensure that only products on days with product modules are present in cart.
 * When using firstAvailable for FilterProductsOnDay, the day of week for the module may change when meals are added
 * and products may no longer be valid.
 * Attempt to move quantity of no-longer-valid products to valid products with same ID.
 * (eg. if product is available on both Monday & Thursday but the available day changes, move quantity to other instance)
 * Also ensure that products are non-renewing if the order is not a subscription.
 * @param products
 * @param productModuleConfigs
 * @param cart
 * @param deliverySchedule
 * @param dispatch
 * @returns Function that reconciles cart products
 */
const ReconcileProducts = (products, productModuleConfigs, cart, deliverySchedule, dispatch) => () => {
    const days = productModuleConfigs.map((config) => GetFilterDayId(config.FilterProductsOnDay, cart, deliverySchedule));
    const validProducts = products.filter((p) => days.includes(p.DayOfWeek));
    const removedProducts = products.filter((p) => !days.includes(p.DayOfWeek));
    // if possible, move quantity of no-longer-valid products to valid products with same ID
    removedProducts.forEach((removedProduct) => {
        let replacement = validProducts.find((product) => product.productId == removedProduct.productId);
        if (replacement && removedProduct.quantity > 0) {
            replacement.quantity += removedProduct.quantity;
        }
        removedProduct.quantity = 0;
    });
    if (!cart.isSubscription) {
        products.forEach((p) => (p.isRenewing = false));
    }
    dispatch({ type: ActionTypes.SET_PRODUCTS, payload: products });
};
const HandleProductChange = (products, filterProductsOnDay, isEditFlow, companyUsesKlaviyo, sku, productId, dayOfWeek, isIncrementing, cart, deliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, reactPixelKey) => {
    var _a, _b;
    const newProducts = products.map((product) => {
        if (product.productId === productId && product.DayOfWeek == dayOfWeek) {
            product.quantity += isIncrementing ? 1 : -1;
            product.DayOfWeek = GetFilterDayId(filterProductsOnDay, cart, deliverySchedule);
        }
        return product;
    });
    const updatedProduct = newProducts.find((p) => p.productId === productId &&
        (p.DayOfWeek === dayOfWeek || (filterProductsOnDay.toLowerCase() === "firstavailable" && p.DayOfWeek === -1)));
    if (updatedProduct && isEditFlow && companyUsesKlaviyo) {
        const eventInfo = {
            ProductName: updatedProduct.productName,
            ProductSKU: updatedProduct.productSKU,
            Quantity: updatedProduct.quantity,
            DayOfWeek: filterProductsOnDay,
            SKU: sku,
        };
        const eventName = (isIncrementing ? "Added" : "Removed") + " Product";
        createKlaviyoEvent(eventName, eventInfo);
    }
    if (reactPixelKey) {
        const title = isIncrementing ? "AddToCart" : "RemoveFromCart";
        const data = {
            content_name: updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.productName,
            content_ids: productId,
            content_type: "product",
            value: (updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.hasSale) ? updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.salePrice : updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.productPrice,
            currency,
        };
        logReactPixel(title, data);
    }
    trackCartItem(isIncrementing ? "add_to_cart" : "remove_from_cart", {
        item_id: String(productId),
        item_name: (_a = updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.productName) !== null && _a !== void 0 ? _a : "",
        price: (_b = ((updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.hasSale) ? updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.salePrice : updatedProduct === null || updatedProduct === void 0 ? void 0 : updatedProduct.productPrice)) !== null && _b !== void 0 ? _b : 0,
        quantity: 1,
        item_category: "product",
    });
    dispatch({ type: ActionTypes.SET_PRODUCTS, payload: newProducts });
};
const HandleRenewSwitch = (products, dispatch) => (productId, dayOfWeek) => {
    const newProducts = products.map((product) => {
        if (product.productId === productId && product.DayOfWeek === dayOfWeek) {
            product.isRenewing = !product.isRenewing;
        }
        return product;
    });
    dispatch({ type: ActionTypes.SET_PRODUCTS, payload: newProducts });
};
/**
 * useProduct - Handles filtering products by day, incrementing/decrementing products, setting their renew state and setting slider settings
 * @param {{moduleConfig: ProductModuleConfiguration}} {moduleConfig}
 * @returns products to render, increment, decrement and renew functions and slider settings
 */
export const useProduct = (sequenceNumber) => {
    var _a;
    const { store, dispatch } = useOrderFlow();
    const { createKlaviyoEvent, logReactPixel } = useLogging();
    const { isCustomizeModalOpen, setIsCustomizeModalOpen } = useContext(CustomizationModalStateContext);
    const moduleConfig = (_a = store.model.Flow.FlowModules.find((mod) => mod.ModuleName === ModuleNames.PRODUCT && mod.Sequence === sequenceNumber && mod.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    const productModuleConfigs = store.model.Flow.FlowModules.filter((fm) => fm.ModuleName == ModuleNames.PRODUCT).map((module) => module.ModuleConfig);
    const ReactPixelKey = store.model.Keys.find((key) => key.KeyName === "FacebookPixelId");
    const { trackCartItem, currency } = useEcommerce();
    return {
        screenXS: moduleConfig.ScreenXS,
        screenSM: moduleConfig.ScreenSM,
        screenMD: moduleConfig.ScreenMD,
        screenLG: moduleConfig.ScreenLG,
        gridSpacing: moduleConfig.GridSpacing,
        isGridLayout: moduleConfig.IsGridLayout,
        headerText: moduleConfig.HeaderText,
        paperSettings: moduleConfig.PaperSetting,
        productsToRender: FilterProducts(store.cart.products, moduleConfig.FilterProductsOnDay, store.cart.daysOfWeek, store.cart, store.model.DeliverySchedule),
        cultureInfo: store.model.CultureInfo,
        currency: store.model.Currency,
        baseImageUrl: store.model.ProductImageBaseUrl,
        isSubscription: store.cart.isSubscription,
        isEditFlow: store.model.IsEditFlow,
        dayOfWeek: GetFilterDayId(moduleConfig.FilterProductsOnDay, store.cart, store.model.DeliverySchedule),
        ReconcileProducts: ReconcileProducts(store.cart.products, productModuleConfigs, store.cart, store.model.DeliverySchedule, dispatch),
        HandleProductIncrement: HandleProductIncrement(store.cart.products, moduleConfig.FilterProductsOnDay, store.model.IsEditFlow, store.model.CompanyUsesKlaviyo, store.model.Sku, store.cart, store.model.DeliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, ReactPixelKey),
        HandleProductDecrement: HandleProductDecrement(store.cart.products, moduleConfig.FilterProductsOnDay, store.model.IsEditFlow, store.model.CompanyUsesKlaviyo, store.model.Sku, store.cart, store.model.DeliverySchedule, dispatch, logReactPixel, createKlaviyoEvent, trackCartItem, currency, ReactPixelKey),
        HandleRenewSwitch: HandleRenewSwitch(store.cart.products, dispatch),
        CenterCards: moduleConfig.CenterCards,
        enableCustomerSorting: moduleConfig.EnableCustomerSorting,
        isCustomizeModalOpen,
        setIsCustomizeModalOpen,
    };
};
